/**
 * BudgetStatusSummary Component
 *
 * 예산 상태를 3가지 범주로 분류하여 요약 표시하는 컴포넌트
 *
 * 책임:
 * - 초과/주의/양호 3가지 상태로 예산 분류
 * - 각 상태에 속하는 모든 카테고리 이름 표시
 * - 색상 코딩으로 시각적 구분
 *
 * 분류 기준:
 * - 초과 (Over): isOverBudget = true
 * - 주의 (Warning): 60% ≤ percentage < 100% (초과 제외)
 * - 양호 (Good): percentage < 60%
 */

import { useMemo } from 'react'
import { findCategoryById } from '@/domains/category/mocks/mockCategories'
import type { BudgetStatus } from '../types/Budget'

/**
 * BudgetStatusSummary Props
 */
export interface BudgetStatusSummaryProps {
  /** 카테고리별 예산 상태 배열 */
  budgetStatuses: BudgetStatus[]
}

/**
 * 상태별 예산 요약 컴포넌트
 *
 * 전체 예산 카드와 카테고리 목록 사이에 배치되는 3개 박스
 */
export function BudgetStatusSummary({ budgetStatuses }: BudgetStatusSummaryProps) {
  /**
   * 예산 상태를 3가지 범주로 분류
   *
   * 분류 우선순위:
   * 1. 초과 여부 체크 (isOverBudget)
   * 2. 사용 비율 체크 (percentage)
   */
  const categorized = useMemo(() => {
    const over: BudgetStatus[] = []
    const warning: BudgetStatus[] = []
    const good: BudgetStatus[] = []

    budgetStatuses.forEach(status => {
      if (status.isOverBudget) {
        // 초과: 예산을 넘긴 경우
        over.push(status)
      } else if (status.percentage >= 60) {
        // 주의: 60% 이상 사용 (초과는 제외)
        warning.push(status)
      } else {
        // 양호: 60% 미만 사용
        good.push(status)
      }
    })

    return { over, warning, good }
  }, [budgetStatuses])

  return (
    <div className="grid grid-cols-3 gap-3">
      {/* 초과 박스 */}
      <StatusBox
        title="초과"
        color="red"
        budgetStatuses={categorized.over}
      />

      {/* 주의 박스 */}
      <StatusBox
        title="주의"
        color="orange"
        budgetStatuses={categorized.warning}
      />

      {/* 양호 박스 */}
      <StatusBox
        title="양호"
        color="green"
        budgetStatuses={categorized.good}
      />
    </div>
  )
}

/**
 * StatusBox Props
 */
interface StatusBoxProps {
  /** 박스 제목 (초과/주의/양호) */
  title: string
  /** 색상 테마 */
  color: 'red' | 'orange' | 'green'
  /** 해당 상태의 예산 목록 */
  budgetStatuses: BudgetStatus[]
}

/**
 * 개별 상태 박스 컴포넌트
 *
 * 책임:
 * - 상태별 개수 표시
 * - 해당 상태의 모든 카테고리 이름 표시 (사용자 요구사항)
 * - 색상 코딩
 */
function StatusBox({ title, color, budgetStatuses }: StatusBoxProps) {
  /**
   * 카테고리 이름 목록 추출
   */
  const categoryNames = useMemo(() => {
    return budgetStatuses.map(status => {
      const category = findCategoryById(status.budget.categoryId)
      return category?.name || '알 수 없음'
    })
  }, [budgetStatuses])

  /**
   * 색상별 Tailwind 클래스 매핑
   */
  const colorClasses = {
    red: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      titleText: 'text-red-700 dark:text-red-300',
      contentText: 'text-red-600 dark:text-red-400',
    },
    orange: {
      bg: 'bg-orange-50 dark:bg-orange-900/20',
      border: 'border-orange-200 dark:border-orange-800',
      titleText: 'text-orange-700 dark:text-orange-300',
      contentText: 'text-orange-600 dark:text-orange-400',
    },
    green: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      titleText: 'text-green-700 dark:text-green-300',
      contentText: 'text-green-600 dark:text-green-400',
    },
  }

  const currentColor = colorClasses[color]

  /**
   * 빈 상태: 해당 상태의 카테고리가 없는 경우
   */
  if (budgetStatuses.length === 0) {
    return (
      <div className={`${currentColor.bg} border ${currentColor.border} rounded-lg p-2`}>
        <div className={`font-bold text-center mb-2 ${currentColor.titleText}`}>
          {title} 0개
        </div>
        <div className={`text-sm text-center ${currentColor.contentText}`}>
          없음
        </div>
      </div>
    )
  }

  /**
   * 메인 컨텐츠: 카테고리 이름 표시
   */
  return (
    <div className={`${currentColor.bg} border ${currentColor.border} rounded-lg p-2`}>
      {/* 제목 + 개수 */}
      <div className={`font-bold text-center mb-2 ${currentColor.titleText}`}>
        {title} {budgetStatuses.length}개
      </div>

      {/* 모든 카테고리 이름 표시 (쉼표로 구분) */}
      <div className={`text-sm ${currentColor.contentText}`}>
        {categoryNames.join(', ')}
      </div>
    </div>
  )
}
